import {assert} from '@pexip/utils';

import type {InputProcessAdaptor} from './types';
import {createMediaStreamTrackProcessor} from '../processor';
import {createMediaStreamTrackGenerator} from '../generator';
import {playVideo, getCanvasRenderingContext2D} from './utils';
import {MediaStreamTrackTransferable} from '../utils';
import {FRAME_RATE} from './constants';

/**
 * An implementation of `InputProcessAdaptor` which uses `MediaStreamTrackGenerator` and
 * `MediaStreamTrackProcessor` APIs to create `ReadableStream` & `WritableStream`
 */
export const createReadWritableStreamInputProcessAdaptor =
    (): InputProcessAdaptor => {
        let outputTrack: MediaStreamTrackGenerator<VideoFrame> | undefined;
        return {
            adapt(track) {
                assert(track.kind === 'video');
                const processor = createMediaStreamTrackProcessor({
                    track: track as MediaStreamVideoTrack,
                });
                outputTrack = createMediaStreamTrackGenerator({
                    kind: 'video',
                });

                return {
                    readable: processor.readable,
                    writable: outputTrack.writable,
                };
            },
            get outputTrack() {
                return outputTrack;
            },
        };
    };

/**
 * Wait for the next frame using the best available API based on
 * page visibility:
 *
 * - Visible + rVFC supported → `requestVideoFrameCallback`
 * - Visible + no rVFC        → `requestAnimationFrame`
 * - Hidden                   → `setTimeout` at the given frame rate
 */
const waitForNextFrame = (
    video: HTMLVideoElement,
    msPerFrame: number,
): Promise<void> => {
    if (document.hidden) {
        return new Promise(r => setTimeout(r, msPerFrame));
    }
    if ('requestVideoFrameCallback' in HTMLVideoElement.prototype) {
        return new Promise(r => video.requestVideoFrameCallback(() => r()));
    }
    return new Promise(r => requestAnimationFrame(() => r()));
};

/**
 * An implementation of `InputProcessAdaptor` which uses `HTMLCanvasElement` or
 * `OffscreenCanvas` API to create `ReadableStream` & `WritableStream`
 */
export const createReadWritableStreamFallbackInputProcessAdaptor = ({
    frameRate,
}: {
    /**
     * Fallback frame rate
     */
    frameRate: number;
}): InputProcessAdaptor => {
    let outputTrack:
        MediaStreamTrackGenerator<VideoFrame> | MediaStreamTrack | undefined;

    return {
        adapt(track) {
            assert(track.kind === 'video');
            const video = document.createElement('video');
            let readableCanvas: OffscreenCanvas;
            let readableCtx: OffscreenCanvasRenderingContext2D;
            let timestamp = 0;
            const fps = track.getSettings().frameRate || frameRate;
            const timeMsPerFrame = 1000 / fps;
            const releaseElements = () => {
                video.width = 0;
                video.height = 0;
                if (readableCanvas) {
                    readableCanvas.width = 0;
                    readableCanvas.height = 0;
                }
            };
            const readable = new ReadableStream<VideoFrame>({
                async start(controller) {
                    const cleanup = () => {
                        controller.close();
                        releaseElements();
                    };
                    track.addEventListener('ended', cleanup, {
                        once: true,
                    });
                    video.srcObject = new MediaStream([track]);
                    video.muted = true;
                    video.playsInline = true;
                    await playVideo(video);
                    readableCanvas = new OffscreenCanvas(
                        video.videoWidth,
                        video.videoHeight,
                    );
                    readableCtx = getCanvasRenderingContext2D(readableCanvas, {
                        alpha: false,
                    });
                    timestamp = performance.now();
                },
                cancel() {
                    releaseElements();
                },
                async pull(controller) {
                    if (track.readyState === 'ended') {
                        releaseElements();
                        return controller.close();
                    }
                    // Wait a bit if too fast, otherwise go ahead for the next frame
                    const diffMs =
                        timeMsPerFrame - (performance.now() - timestamp);
                    if (diffMs) {
                        await waitForNextFrame(video, diffMs);
                        // @ts-expect-error  --- sth is wrong with type narrowing
                        if (track.readyState === 'ended') {
                            releaseElements();
                            return controller.close();
                        }
                    }
                    if (readableCanvas.width !== video.videoWidth) {
                        readableCanvas.width = video.videoWidth;
                    }
                    if (readableCanvas.height !== video.videoHeight) {
                        readableCanvas.height = video.videoHeight;
                    }
                    timestamp = performance.now();
                    readableCtx.drawImage(video, 0, 0);
                    controller.enqueue(
                        new VideoFrame(readableCanvas, {
                            // VideoFrame timestamp is in microseconds
                            // https://developer.mozilla.org/en-US/docs/Web/API/VideoFrame/timestamp
                            timestamp: timestamp * 1000,
                        }),
                    );
                },
            });

            // captureStream API is only available in HTMLCanvasElement
            const writableCanvas = document.createElement('canvas');
            const writableCtx = getCanvasRenderingContext2D(writableCanvas, {
                alpha: false,
                desynchronized: true,
            });
            const [capturedVideoTrack] = writableCanvas
                .captureStream(frameRate)
                .getVideoTracks();
            outputTrack = capturedVideoTrack;
            const release = () => {
                capturedVideoTrack?.stop();
                writableCanvas.width = 0;
                writableCanvas.height = 0;
            };
            const writable = new WritableStream<VideoFrame>({
                abort() {
                    release();
                },
                close() {
                    release();
                },
                write(frame) {
                    if (writableCanvas.width !== frame.displayWidth) {
                        writableCanvas.width = frame.displayWidth;
                    }
                    if (writableCanvas.height !== frame.displayHeight) {
                        writableCanvas.height = frame.displayHeight;
                    }
                    writableCtx.drawImage(
                        frame,
                        0,
                        0,
                        writableCanvas.width,
                        writableCanvas.height,
                    );
                    frame.close();
                },
            });

            return {
                readable,
                writable,
            };
        },
        get outputTrack() {
            return outputTrack;
        },
    };
};

/**
 * An implementation of `InputProcessAdaptor` which adapts `MediaStreamTrack`
 * directly as specified from the spec https://w3c.github.io/mediacapture-transform
 */
export const createMediaStreamTrackInputProcessAdaptor =
    (): InputProcessAdaptor => {
        return {
            adapt(track) {
                return track;
            },
        };
    };

/**
 * Get the supported InputProcessAdaptor based on feature detection
 * and preference provided
 *
 * @param tryNativeAPI - Whether or not to try to use the Native MediaStreamTrackProcessor APIs
 * @param targetFrameRate - The target frame rate to use when fallback implementation of
 * video frame extraction rate
 */
export const getDefaultInputProcessAdaptor = (
    tryNativeAPI = true,
    targetFrameRate = FRAME_RATE,
): InputProcessAdaptor => {
    if (tryNativeAPI) {
        if (MediaStreamTrackTransferable) {
            return createMediaStreamTrackInputProcessAdaptor();
        } else if ('MediaStreamTrackProcessor' in window) {
            return createReadWritableStreamInputProcessAdaptor();
        }
    }
    return createReadWritableStreamFallbackInputProcessAdaptor({
        frameRate: targetFrameRate,
    });
};
